/**
 * The console-wide light/dark toggle: a `data-theme` attribute on <html>,
 * persisted per browser. Both token sets react to it — `tokens.css` for the
 * console chrome, `comms.css` for the Communications module.
 *
 * The framed pages are exempt: there the theme belongs to the dashboard,
 * arriving over the postMessage bridge onto the embed host element instead.
 */

export type ConsoleTheme = 'light' | 'dark';

const STORAGE_KEY = 'console.theme';

export function storedTheme(): ConsoleTheme {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function applyTheme(theme: ConsoleTheme): void {
  document.documentElement.dataset.theme = theme;
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Private mode — the toggle still works for this page view.
  }
}

/**
 * The pages the dispatch dashboard frames. The light token set lives on
 * `:root` alone, so a stale DARK preference on <html> would beat a host
 * element the bridge marked light — every framed page is exempt, not only
 * `/embed`.
 */
const FRAMED = ['/embed', '/dashboard-embed', '/website-embed'];

/** Called once at boot, before first paint. */
export function initTheme(pathname: string): void {
  if (FRAMED.some((path) => pathname === path || pathname.startsWith(`${path}/`))) {
    // The bridge owns the embed's theme; a stale local preference on <html>
    // would fight the host element's data-theme.
    delete document.documentElement.dataset.theme;
    return;
  }
  document.documentElement.dataset.theme = storedTheme();
}
